import type { Locale } from "./types";

/** Teksty samego interfejsu. Treść pytań przychodzi z backendu, już przetłumaczona. */
const STRINGS = {
  otpTitle: {
    pl: "Potwierdź numer telefonu",
    en: "Confirm your phone number",
    ru: "Подтвердите номер телефона",
    uk: "Підтвердіть номер телефону",
  },
  otpSubtitle: {
    pl: "Wyślemy SMS z kodem na numer, na który dostałeś link.",
    en: "We'll text a code to the number that received the link.",
    ru: "Вышлем SMS с кодом на номер, на который пришла ссылка.",
    uk: "Надішлемо SMS з кодом на номер, на який надійшло посилання.",
  },
  otpSent: {
    pl: "Kod wysłany SMS-em. Wpisz go poniżej. SMS bywa opóźniony — każdy wysłany kod działa przez 10 minut.",
    en: "Code sent by SMS. Enter it below. Texts can be delayed — every code we send works for 10 minutes.",
    ru: "Код отправлен по SMS. Введите его ниже. SMS может задерживаться — каждый отправленный код действует 10 минут.",
    uk: "Код надіслано в SMS. Введіть його нижче. SMS може затримуватися — кожен надісланий код діє 10 хвилин.",
  },
  // Odliczanie do ponownej wysyłki; `{s}` to liczba sekund.
  resendIn: {
    pl: "Wyślij ponownie ({s} s)",
    en: "Resend ({s} s)",
    ru: "Выслать снова ({s} с)",
    uk: "Надіслати ще раз ({s} с)",
  },
  send: { pl: "Wyślij kod", en: "Send code", ru: "Выслать код", uk: "Надіслати код" },
  resend: { pl: "Wyślij ponownie", en: "Resend", ru: "Выслать снова", uk: "Надіслати ще раз" },
  verify: { pl: "Zweryfikuj", en: "Verify", ru: "Подтвердить", uk: "Підтвердити" },
  codePlaceholder: { pl: "Kod 6-cyfrowy", en: "6-digit code", ru: "6-значный код", uk: "6-значний код" },
  save: { pl: "Zapisz", en: "Save", ru: "Сохранить", uk: "Зберегти" },
  saving: { pl: "Zapisuję…", en: "Saving…", ru: "Сохраняю…", uk: "Зберігаю…" },
  skip: { pl: "Pomiń", en: "Skip", ru: "Пропустить", uk: "Пропустити" },
  demoBanner: {
    pl: "Wersja demonstracyjna — nie wprowadzaj danych prawdziwych osób. Kod SMS pokazuje się na ekranie.",
    en: "Demo version — do not enter real people's data. The SMS code is shown on screen.",
    ru: "Демоверсия — не вводите данные реальных людей. Код из SMS показан на экране.",
    uk: "Демоверсія — не вводьте дані реальних людей. Код із SMS показано на екрані.",
  },
  clear: { pl: "Wyczyść", en: "Clear", ru: "Очистить", uk: "Очистити" },
  search: { pl: "Szukaj…", en: "Search…", ru: "Поиск…", uk: "Пошук…" },
  noResults: { pl: "Brak wyników", en: "No matches", ru: "Ничего не найдено", uk: "Нічого не знайдено" },
  choosePhoto: { pl: "Zrób zdjęcie", en: "Take a photo", ru: "Сделать фото", uk: "Зробити фото" },
  chooseFile: { pl: "Wybierz plik", en: "Choose a file", ru: "Выбрать файл", uk: "Обрати файл" },
  uploading: { pl: "Wysyłam…", en: "Uploading…", ru: "Загружаю…", uk: "Завантажую…" },
  filesUploadedCount: {
    pl: "Wgrane strony:", en: "Uploaded pages:", ru: "Загружено страниц:", uk: "Завантажено сторінок:",
  },
  filesAddAnother: {
    pl: "Dodaj kolejną stronę", en: "Add another page",
    ru: "Добавить ещё страницу", uk: "Додати ще сторінку",
  },
  filesNeedMore: {
    pl: "Wgraj wszystkie strony dokumentu.", en: "Upload every page of the document.",
    ru: "Загрузите все страницы документа.", uk: "Завантажте всі сторінки документа.",
  },
  filesDone: { pl: "Gotowe, dalej", en: "Done, continue", ru: "Готово, далее", uk: "Готово, далі" },
  startKyc: {
    pl: "Rozpocznij weryfikację tożsamości",
    en: "Start identity verification",
    ru: "Начать проверку личности",
    uk: "Почати перевірку особи",
  },
  kycRunning: {
    pl: "Weryfikacja otworzy się tutaj. Po zakończeniu przejdziemy dalej automatycznie.",
    en: "Verification opens here. We'll continue automatically once it's done.",
    ru: "Проверка откроется здесь. После завершения продолжим автоматически.",
    uk: "Перевірка відкриється тут. Після завершення продовжимо автоматично.",
  },
  // Wyjście awaryjne, gdy ramka dostawcy się nie wyświetli. Krótkie, bo stoi
  // w pasku nad ramką — na telefonie to jedyne miejsce, które widać.
  kycOpenInTab: {
    pl: "Otwórz w nowej karcie",
    en: "Open in a new tab",
    ru: "Открыть в новой вкладке",
    uk: "Відкрити в новій вкладці",
  },
  kycMockNote: {
    pl: "Tryb demonstracyjny — dostawca KYC nie jest podpięty.",
    en: "Demo mode — the KYC provider is not connected.",
    ru: "Демо-режим — провайдер KYC не подключён.",
    uk: "Демо-режим — провайдер KYC не підключений.",
  },
  kycSimulate: { pl: "Zakończ weryfikację", en: "Finish verification", ru: "Завершить проверку", uk: "Завершити перевірку" },
  kycDenied: {
    pl: "Weryfikacja nie powiodła się. Spróbuj ponownie przy lepszym świetle albo wgraj zdjęcia dokumentu.",
    en: "Verification failed. Try again in better light or upload photos of the document.",
    ru: "Проверка не прошла. Попробуйте при лучшем освещении или загрузите фото документа.",
    uk: "Перевірка не пройшла. Спробуйте при кращому освітленні або завантажте фото документа.",
  },
  kycWrongDocument: {
    pl: "To nie jest dokument, o który prosimy. Zeskanuj właściwy dokument — wydany w wymaganym kraju.",
    en: "This is not the document we ask for. Please scan the right one — issued in the required country.",
    ru: "Это не тот документ, который мы просим. Отсканируйте нужный документ — выданный в требуемой стране.",
    uk: "Це не той документ, який ми просимо. Відскануйте потрібний документ — виданий у вимаганій країні.",
  },
  kycExpired: {
    pl: "Weryfikacja trwała zbyt długo i wygasła. Możesz zacząć ją od nowa albo wgrać zdjęcia dokumentu.",
    en: "Verification took too long and expired. Start again or upload photos of the document.",
    ru: "Проверка длилась слишком долго и истекла. Начните заново или загрузите фото документа.",
    uk: "Перевірка тривала занадто довго й закінчилася. Почніть заново або завантажте фото документа.",
  },
  kycRetry: { pl: "Spróbuj ponownie", en: "Try again", ru: "Попробовать снова", uk: "Спробувати знову" },
  kycUploadInstead: {
    pl: "Wgraj zdjęcia dokumentu",
    en: "Upload document photos",
    ru: "Загрузить фото документа",
    uk: "Завантажити фото документа",
  },
  viewDocument: { pl: "Zobacz dokument", en: "View document", ru: "Посмотреть документ", uk: "Переглянути документ" },
  documentError: {
    pl: "Nie udało się otworzyć dokumentu. Spróbuj ponownie.",
    en: "Couldn't open the document. Please try again.",
    ru: "Не удалось открыть документ. Попробуйте ещё раз.",
    uk: "Не вдалося відкрити документ. Спробуйте ще раз.",
  },
  // Obiecywało wysyłkę kopii e-mailem, a w projekcie nie ma żadnej wysyłki
  // poczty. Mówimy to, co system faktycznie robi.
  signHere: {
    pl: "Podpisz palcem lub myszką. Podpisany dokument zapiszemy w Twoich aktach.",
    en: "Sign with your finger or mouse. We'll keep the signed document in your file.",
    ru: "Подпишите пальцем или мышью. Подписанный документ сохраним в вашем деле.",
    uk: "Підпишіть пальцем або мишею. Підписаний документ збережемо у вашій справі.",
  },
  signSave: { pl: "Podpisz i zapisz", en: "Sign and save", ru: "Подписать и сохранить", uk: "Підписати та зберегти" },
  signed: { pl: "Podpisano", en: "Signed", ru: "Подписано", uk: "Підписано" },
  helpButton: {
    pl: "Pomocy — utknąłem",
    en: "Help — I'm stuck",
    ru: "Помогите — я застрял",
    uk: "Допоможіть — я застряг",
  },
  helpCommentLabel: {
    pl: "W czym możemy pomóc? (nieobowiązkowe)",
    en: "How can we help? (optional)",
    ru: "Чем мы можем помочь? (необязательно)",
    uk: "Чим ми можемо допомогти? (необов'язково)",
  },
  helpSend: {
    pl: "Wyślij prośbę",
    en: "Send request",
    ru: "Отправить запрос",
    uk: "Надіслати запит",
  },
  helpCancel: {
    pl: "Anuluj",
    en: "Cancel",
    ru: "Отмена",
    uk: "Скасувати",
  },
  helpSent: {
    pl: "Dostaliśmy zgłoszenie. Odezwiemy się — możesz zamknąć stronę.",
    en: "We've got your request. We'll get in touch — you can close the page.",
    ru: "Мы получили обращение. Свяжемся с вами — страницу можно закрыть.",
    uk: "Ми отримали звернення. Зв'яжемося з вами — сторінку можна закрити.",
  },
  signedDocuments: {
    pl: "Twoje podpisane dokumenty",
    en: "Your signed documents",
    ru: "Ваши подписанные документы",
    uk: "Ваші підписані документи",
  },
  notApplicable: { pl: "nie dotyczy", en: "not applicable", ru: "не применимо", uk: "не застосовно" },
  close: { pl: "Zamknij", en: "Close", ru: "Закрыть", uk: "Закрити" },
  loading: { pl: "Wczytuję…", en: "Loading…", ru: "Загружаю…", uk: "Завантажую…" },
  offline: {
    pl: "Jesteś offline. Wypełnione odpowiedzi są zapisane — wróć, gdy będzie internet.",
    en: "You're offline. Your answers are saved — come back when you're online.",
    ru: "Вы офлайн. Ответы сохранены — вернитесь, когда появится интернет.",
    uk: "Ви офлайн. Відповіді збережено — поверніться, коли буде інтернет.",
  },
  linkBroken: {
    pl: "Ten link jest nieprawidłowy. Poproś rekrutera o nowy.",
    en: "This link is not valid. Ask your recruiter for a new one.",
    ru: "Эта ссылка недействительна. Попросите рекрутера прислать новую.",
    uk: "Це посилання недійсне. Попросіть рекрутера надіслати нове.",
  },
  finished: { pl: "Gotowe", en: "All done", ru: "Готово", uk: "Готово" },
  installHint: {
    pl: "Dodaj do ekranu głównego, żeby wrócić jednym kliknięciem.",
    en: "Add to your home screen to come back in one tap.",
    ru: "Добавьте на главный экран, чтобы вернуться одним касанием.",
    uk: "Додайте на головний екран, щоб повернутися одним дотиком.",
  },
  install: { pl: "Dodaj", en: "Add", ru: "Добавить", uk: "Додати" },
  dismiss: { pl: "Nie teraz", en: "Not now", ru: "Не сейчас", uk: "Не зараз" },
  motywJasny: { pl: "Motyw jasny", en: "Light theme", ru: "Светлая тема", uk: "Світла тема" },
  motywCiemny: { pl: "Motyw ciemny", en: "Dark theme", ru: "Тёмная тема", uk: "Темна тема" },
  jezyk: { pl: "Język", en: "Language", ru: "Язык", uk: "Мова" },
  required: { pl: "To pole jest wymagane.", en: "This field is required.", ru: "Это поле обязательно.", uk: "Це поле обов'язкове." },
  autofilled: {
    pl: "Uzupełniliśmy {n} pól za Ciebie — sprawdź, czy się zgadzają.",
    en: "We filled in {n} fields for you — check they're right.",
    ru: "Мы заполнили {n} полей за вас — проверьте, всё ли верно.",
    uk: "Ми заповнили {n} полів за вас — перевірте, чи все правильно.",
  },
  suggested: { pl: "podpowiedź", en: "suggested", ru: "подсказка", uk: "підказка" },
  flatNumber: {
    pl: "Nr lokalu (opcjonalnie)",
    en: "Flat no. (optional)",
    ru: "Номер квартиры (необязательно)",
    uk: "Номер квартири (необов'язково)",
  },
  flatNumberHint: {
    pl: "Numer mieszkania wpisz tutaj, nie w polu adresu.",
    en: "Enter the flat number here, not in the address field.",
    ru: "Номер квартиры вводите здесь, а не в поле адреса.",
    uk: "Номер квартири вводьте тут, а не в полі адреси.",
  },
  addressChosen: {
    pl: "Adres wybrany — dopisz numer mieszkania i przejdź dalej.",
    en: "Address selected — add the flat number and continue.",
    ru: "Адрес выбран — добавьте номер квартиры и продолжите.",
    uk: "Адресу вибрано — додайте номер квартири та продовжте.",
  },
  yourAnswers: {
    pl: "Twoje odpowiedzi",
    en: "Your answers",
    ru: "Ваши ответы",
    uk: "Ваші відповіді",
  },
  moreAnswers: { pl: "wcześniejszych", en: "earlier", ru: "ранее", uk: "раніше" },
  hideAnswers: { pl: "zwiń", en: "collapse", ru: "свернуть", uk: "згорнути" },
  back: { pl: "Popraw poprzednią odpowiedź", en: "Change the previous answer",
          ru: "Изменить предыдущий ответ", uk: "Змінити попередню відповідь" },
  stepOf: { pl: "Krok", en: "Step", ru: "Шаг", uk: "Крок" },
  stepOfMid: { pl: "z", en: "of", ru: "из", uk: "з" },
  // Ekran twierdził, że kopia „poszła na e-mail" — a poczty nie wysyłamy
  // w ogóle i wysyłać nie będziemy. Dokumenty są teraz widoczne wyżej, więc
  // to zdanie jest prawdziwe niezależnie od tego, kto i kiedy się odezwie.
  outcomeDoneNext: {
    pl: "Podpisane dokumenty masz powyżej — możesz je pobrać. Zwykle odpowiadamy w ciągu 2 dni roboczych.",
    en: "Your signed documents are above — you can download them. We usually reply within 2 working days.",
    ru: "Подписанные документы выше — их можно скачать. Обычно отвечаем в течение 2 рабочих дней.",
    uk: "Підписані документи вище — їх можна завантажити. Зазвичай відповідаємо протягом 2 робочих днів.",
  },
  // Wariant przy finale marki: termin odpowiedzi niesie już tekst z szablonu.
  outcomeDocsNext: {
    pl: "Podpisane dokumenty masz powyżej — możesz je pobrać.",
    en: "Your signed documents are above — you can download them.",
    ru: "Подписанные документы выше — их можно скачать.",
    uk: "Підписані документи вище — їх можна завантажити.",
  },
  outcomeBlockedNext: {
    pl: "Napisz do nas na biuro@city-drive.pl — sprawdzimy Twoją sytuację indywidualnie i podpowiemy, co dalej.",
    en: "Write to biuro@city-drive.pl — we'll look at your case individually and suggest what to do next.",
    ru: "Напишите на biuro@city-drive.pl — разберём вашу ситуацию отдельно и подскажем, что делать дальше.",
    uk: "Напишіть на biuro@city-drive.pl — розберемо вашу ситуацію окремо й підкажемо, що робити далі.",
  },
  outcomeBack: {
    pl: "Wróć i zmień ostatnią odpowiedź",
    en: "Go back and change the last answer",
    ru: "Вернуться и изменить последний ответ",
    uk: "Повернутися й змінити останню відповідь",
  },
  finishedSuccessTitle: {
    pl: "Dziękujemy za przesłanie danych.",
    en: "Thank you for submitting your data.",
    ru: "Спасибо за предоставленные данные.",
    uk: "Дякуємо за надання даних.",
  },
  finishedFailTitle: {
    pl: "Niestety na podstawie podanych informacji nie możemy zaoferować zatrudnienia. Prosimy o kontakt z obsługą klienta: +48 730 000 310.",
    en: "Unfortunately, we are unable to offer you employment based on the provided information. Please contact customer support: +48 730 000 310.",
    ru: "К сожалению, на основе предоставленных данных мы не можем предложить вам трудоустройство. Пожалуйста, свяжитесь со службой поддержки: +48 730 000 310.",
    uk: "На жаль, на основі наданих даних ми не можемо запропонувати вам працевлаштування. Будь ласка, зверніться до служби підтримки: +48 730 000 310.",
  },
  // Odmowa dla marki bez własnego tekstu: bez infolinii City Drive, która
  // stoi w tekście domyślnym — kandydat innej marki dzwoniłby do obcej firmy.
  finishedFailTitleGeneric: {
    pl: "Niestety na podstawie podanych informacji nie możemy zaoferować zatrudnienia. W razie pytań skontaktuj się z osobą, która przesłała Ci link.",
    en: "Unfortunately, we are unable to offer you employment based on the provided information. If you have questions, contact the person who sent you the link.",
    ru: "К сожалению, на основе предоставленных данных мы не можем предложить вам трудоустройство. Если у вас есть вопросы, свяжитесь с тем, кто отправил вам ссылку.",
    uk: "На жаль, на основі наданих даних ми не можемо запропонувати вам працевлаштування. Якщо маєте запитання, зверніться до людини, яка надіслала вам посилання.",
  },
  asideTitle: {
    pl: "Dokończ rejestrację w kilku krokach",
    en: "Finish your registration in a few steps",
    ru: "Завершите регистрацию за несколько шагов",
    uk: "Завершіть реєстрацію за кілька кроків",
  },
  asideLead: {
    pl: "Pytamy o jedną rzecz naraz. Dane z dokumentu wpiszemy za Ciebie, a postęp zapisujemy na bieżąco.",
    en: "We ask one thing at a time. We fill in document details for you and save your progress as you go.",
    ru: "Спрашиваем по одному. Данные из документа заполним за вас, прогресс сохраняем автоматически.",
    uk: "Питаємо по одному. Дані з документа заповнимо за вас, прогрес зберігаємо автоматично.",
  },
  asideProgress: {
    pl: "wypełnione",
    en: "complete",
    ru: "заполнено",
    uk: "заповнено",
  },
  ocrUnreadable: {
    pl: "Nie odczytaliśmy danych z tego zdjęcia. Zrób je jeszcze raz przy lepszym świetle.",
    en: "We couldn't read this photo. Take it again in better light.",
    ru: "Не удалось распознать это фото. Сделайте его заново при лучшем свете.",
    uk: "Не вдалося розпізнати це фото. Зробіть його ще раз при кращому світлі.",
  },
  signatureLabel: { pl: "Podpis", en: "Signature", ru: "Подпись", uk: "Підпис" },
  startTitle: {
    pl: "Zacznij od linku z SMS-a",
    en: "Start from the link in your SMS",
    ru: "Начните со ссылки из SMS",
    uk: "Почніть із посилання в SMS",
  },
  startText: {
    pl: "Link do Twojego onboardingu wysyłamy SMS-em. Otwórz go na telefonie albo wklej poniżej.",
    en: "We text you a link to your onboarding. Open it on your phone or paste it below.",
    ru: "Ссылку на онбординг мы отправляем по SMS. Откройте её на телефоне или вставьте ниже.",
    uk: "Посилання на онбординг надсилаємо в SMS. Відкрийте його на телефоні або вставте нижче.",
  },
  startPlaceholder: {
    pl: "Wklej link lub ID onboardingu",
    en: "Paste the link or onboarding ID",
    ru: "Вставьте ссылку или ID онбординга",
    uk: "Вставте посилання або ID онбордингу",
  },
  startOpen: { pl: "Otwórz", en: "Open", ru: "Открыть", uk: "Відкрити" },
  startBadId: {
    pl: "Nie rozpoznajemy tego linku. Skopiuj go w całości z SMS-a.",
    en: "We don't recognise this link. Copy the whole thing from the SMS.",
    ru: "Не распознаём эту ссылку. Скопируйте её целиком из SMS.",
    uk: "Не розпізнаємо це посилання. Скопіюйте його повністю з SMS.",
  },
  startDevHint: {
    pl: "Środowisko lokalne — możesz założyć testowy onboarding bez CRM-u.",
    en: "Local environment — you can create a test onboarding without the CRM.",
    ru: "Локальная среда — можно создать тестовый онбординг без CRM.",
    uk: "Локальне середовище — можна створити тестовий онбординг без CRM.",
  },
  startDevButton: {
    pl: "Utwórz testowy onboarding",
    en: "Create a test onboarding",
    ru: "Создать тестовый онбординг",
    uk: "Створити тестовий онбординг",
  },
  devCode: { pl: "Kod (tryb dev):", en: "Code (dev mode):", ru: "Код (dev-режим):", uk: "Код (dev-режим):" },
} as const;

export type StringKey = keyof typeof STRINGS;

export function t(key: StringKey, locale: Locale): string {
  const bundle = STRINGS[key] as Record<Locale, string>;
  return bundle[locale] ?? bundle.pl;
}

export const LOCALES: { id: Locale; label: string }[] = [
  { id: "pl", label: "Polski" },
  { id: "en", label: "English" },
  { id: "ru", label: "Русский" },
  { id: "uk", label: "Українська" },
];
